/**
 * The kitchen in the room, through the phone.
 *
 * Apple's AR Quick Look takes a USDZ file and stands it on the floor in front of you at
 * true size. three.js writes USDZ. So: the cabinets, benchtop and handles as they are in
 * the scene -- not the room, because the room is the one you are standing in -- with the
 * kitchen's floor put at zero and its middle at the origin, so it appears where you point
 * and not four metres to the left.
 *
 * Only where it works: iPhone and iPad, in Safari or in the app. Elsewhere the same file
 * downloads, which is still a model of the kitchen for anyone with a viewer.
 */
import { shareFile, download, isNative } from './share.js'
import { exportModel } from './model.js'

export function arSupported() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const iOS = /iP(hone|ad|od)/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (!iOS) return false
  if (isNative()) return true
  // Safari on iOS; the rel="ar" link is a Safari feature and a WKWebView in another app may not have it.
  const a = document.createElement('a')
  return a.relList?.supports?.('ar') ?? /Safari/.test(ua)
}

/**
 * The cabinets as a USDZ blob, centred and on the floor.
 *
 * The geometry lives in model.js now, which does the same job for glTF, OBJ and STL. This
 * stays because AR is not an export -- it is a way of LOOKING at the kitchen, and the file
 * is an implementation detail of Quick Look.
 */
export async function exportUsdz(kitchen) {
  return (await exportModel(kitchen, 'usdz')).blob
}

/** Open it in Quick Look where that exists; share or download the file where it does not. */
export async function viewInAr(blob, filename = 'kitchen.usdz') {
  if (isNative()) return shareFile(blob, filename, { title: 'Your kitchen, in AR' })
  const a = document.createElement('a')
  if (a.relList?.supports?.('ar')) {
    a.rel = 'ar'
    a.href = URL.createObjectURL(blob)
    a.appendChild(document.createElement('img'))   // Safari needs an image child to treat it as AR
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.setTimeout(() => URL.revokeObjectURL(a.href), 20000)
    return 'opened'
  }
  download(blob, filename)
  return 'downloaded'
}
